import ErrorIcon from "../../assets/error.svg?raw"
import { i18n } from "../../internationalize/i18n.service"
import { getErrorMessage } from "../../core/helpers"
import { AuthService } from "../../auth/services/auth.service"
import type { ProfileService } from "../profile.service"
import { FeedbackService } from "../../newsletters/userMessage.service"

import "./DeleteAccount.styles.scss"

export class DeleteAccountComponent extends HTMLElement {
    private authService: AuthService = window.tbwServices.authService
    private profileService: ProfileService = window.tbwServices.profileService
    private feedbackService!: FeedbackService

    private triggerButton!: HTMLButtonElement
    private dialog!: HTMLDialogElement
    private confirmButton!: HTMLButtonElement
    private cancelButton!: HTMLButtonElement
    private dialogErrorContainer!: HTMLDivElement

    constructor() {
        super()
        this.innerHTML = this.render()

        this.triggerButton = this.querySelector(".js-delete-trigger")!
        this.dialog = this.querySelector(".js-delete-dialog")!
        this.confirmButton = this.querySelector(".js-delete-confirm")!
        this.cancelButton = this.querySelector(".js-delete-cancel")!
        this.dialogErrorContainer = this.querySelector(".js-dialog-error")!
    }

    connectedCallback() {
        if (!this.authService.isAuthenticated) {
            this.showSignInPrompt()
            return
        }

        this.feedbackService = new FeedbackService(this)
        this.setupEventListeners()
    }

    private showSignInPrompt(): void {
        const prompt = document.createElement("div")
        prompt.className = "sign-in-prompt"
        prompt.textContent =
            i18n.getLanguage() === "fr"
                ? "Connectez-vous pour supprimer votre compte"
                : "Sign in to delete your account"
        this.appendChild(prompt)
    }

    private render(): string {
        const triggerLabel =
            i18n.getLanguage() === "fr" ? "Supprimer mon compte" : "Delete my account"
        const message =
            i18n.getLanguage() === "fr"
                ? "Êtes-vous sûr de vouloir supprimer votre compte ? Cette action est irréversible."
                : "Are you sure you want to delete your account? This action cannot be undone."
        const cancelLabel =
            i18n.getLanguage() === "fr" ? "Annuler" : "Cancel"
        const confirmLabel =
            i18n.getLanguage() === "fr" ? "Supprimer" : "Delete"

        return `
            <button type="button" class="delete-account-button js-delete-trigger">${triggerLabel}</button>
            <dialog class="delete-account-dialog js-delete-dialog">
                <div class="dialog-content">
                    <p class="dialog-message">${message}</p>
                    <div class="js-dialog-error dialog-error"></div>
                    <div class="dialog-actions">
                        <button type="button" class="cancel-button js-delete-cancel">${cancelLabel}</button>
                        <button type="button" class="confirm-button js-delete-confirm">${confirmLabel}</button>
                    </div>
                </div>
            </dialog>
        `
    }

    private setupEventListeners(): void {
        this.triggerButton.addEventListener("click", () => this.openModal())
        this.cancelButton.addEventListener("click", () => this.closeModal())
        this.confirmButton.addEventListener("click", () => this.handleConfirm())
        this.dialog.addEventListener("cancel", () => this.closeModal())
    }

    private openModal(): void {
        this.feedbackService.hideMessages()
        this.clearDialogError()
        this.dialog.showModal()
        document.body.style.overflow = "hidden"
    }

    private showDialogError(message: string): void {
        this.dialogErrorContainer.innerHTML = `${ErrorIcon} ${message}`
        this.dialogErrorContainer.className = "dialog-error dialog-error-visible"
    }

    private clearDialogError(): void {
        this.dialogErrorContainer.innerHTML = ""
        this.dialogErrorContainer.className = "dialog-error"
    }

    private closeModal(): void {
        this.dialog.close()
        document.body.style.overflow = ""
    }

    private async handleConfirm(): Promise<void> {
        this.feedbackService.hideMessages()
        this.feedbackService.setSubmitButtonLoading(this.confirmButton, true)

        try {
            await this.profileService.deleteAccount()
            this.closeModal()
            this.authService.logout()
            this.feedbackService.reloadPageWithTimeout()
        } catch (error: unknown) {
            const fallback = i18n.getLanguage() === "fr"
                ? "Erreur lors de la suppression du compte"
                : "Error deleting account"
            this.showDialogError(getErrorMessage(error, fallback))
        } finally {
            this.feedbackService.setSubmitButtonLoading(this.confirmButton, false)
        }
    }
}
